/**
 * "Tell me what to change" — a conversation, not a contact form.
 *
 * It used to be a `mailto:` form: the note left through the visitor's mail app,
 * nothing was stored, and the confirmation could only promise that some other
 * program had opened. A prospect who has not paid anything is exactly the person
 * least likely to finish that. So this posts to `/api/invite/:token/messages`,
 * which writes to the very same thread they will see in the portal after they
 * claim the site — the first message of the relationship, kept.
 *
 * What it deliberately does NOT do: fake presence. No typing dots, no "online
 * now", no auto-reply pretending to be a person. The greeting is canned and
 * reads as canned; everything after it is real and polled.
 */

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

export interface ChatMessage {
  id: string;
  sender: 'customer' | 'team';
  body: string;
  createdAt: string;
  /** Local-only: shown while the POST is in flight. */
  pending?: boolean;
}

const POLL_MS = 20_000;
const MAX_LENGTH = 4000;

function timeLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const mins = Math.round((Date.now() - date.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)} hr ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Presentation only, from the invite page's "Work Order" kit (`wo.css`,
 * scoped under `.wo-page`). The logic below — the thread, the poll, the
 * optimistic send and the token endpoints — is untouched by the redesign.
 */
function Bubble({ message }: { message: ChatMessage }) {
  const mine = message.sender === 'customer';

  return (
    <li className={`wo-msg${mine ? ' wo-msg--mine' : ''}`}>
      {!mine && <div className="wo-av">M</div>}
      <div>
        <div className="wo-who">
          {mine ? 'You' : 'Mikhail · Foundlio'} ·{' '}
          {message.pending ? 'sending…' : timeLabel(message.createdAt)}
        </div>
        <div className="wo-bubble">{message.body}</div>
      </div>
    </li>
  );
}

export interface ChangeChatProps {
  token?: string;
  /**
   * The invite API base — `/api/invite/<token>`, or `/api/invite/me` on the
   * tokenless /claim page (fnd-mk2x: without it a claim visitor's change
   * request took the demo branch and was never sent). Defaults from `token`.
   */
  apiBase?: string;
  businessName: string;
  supportEmail: string;
  /** Demo invites keep the whole exchange local — nothing is posted. */
  isDemo?: boolean;
}

export default function ChangeChat({
  token,
  apiBase,
  businessName,
  supportEmail,
  isDemo = false,
}: ChangeChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLUListElement | null>(null);

  const base = apiBase ?? (token ? `/api/invite/${token}` : undefined);

  const greeting = `Hi — Mikhail here, I built this one for ${businessName}. Wrong hours, a service you don't offer, a photo you hate? Tell me and I'll change it before you decide anything.`;

  /** Load the thread, then keep it fresh while the page is open. */
  useEffect(() => {
    if (!base || isDemo) return;
    let active = true;

    const load = async () => {
      try {
        const response = await axios.get(`${base}/messages`);
        if (!active) return;
        const next = (response.data?.data?.messages ?? []) as ChatMessage[];
        // A poll landing mid-send must not wipe the optimistic bubble.
        setMessages(current => {
          const pending = current.filter(message => message.pending);
          return [...next, ...pending];
        });
      } catch {
        /* An unreachable history is not worth an error state — the composer still works. */
      }
    };

    load();
    const timer = window.setInterval(load, POLL_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [base, isDemo]);

  // Newest at the bottom — but only scroll the transcript, never the page.
  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages.length]);

  async function send() {
    const body = draft.trim();
    if (!body || sending) return;

    const optimistic: ChatMessage = {
      id: `pending-${Date.now()}`,
      sender: 'customer',
      body,
      createdAt: new Date().toISOString(),
      pending: true,
    };

    setMessages(current => [...current, optimistic]);
    setDraft('');
    setError(null);
    setSending(true);

    if (isDemo || !base) {
      window.setTimeout(() => {
        setMessages(current =>
          current.map(message =>
            message.id === optimistic.id ? { ...message, pending: false } : message
          )
        );
        setSending(false);
      }, 500);
      return;
    }

    try {
      const response = await axios.post(`${base}/messages`, { body });
      const saved = response.data?.data as ChatMessage | undefined;
      setMessages(current =>
        current.map(message =>
          message.id === optimistic.id
            ? { ...(saved ?? message), pending: false }
            : message
        )
      );
    } catch (requestError) {
      setMessages(current => current.filter(message => message.id !== optimistic.id));
      setDraft(body);
      setError(
        (axios.isAxiosError(requestError) && requestError.response?.data?.error) ||
          'That did not go through. Try again, or write to ' + supportEmail + '.'
      );
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends; Shift+Enter is a new line. Matches every chat they already use.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  }

  const said = messages.some(message => message.sender === 'customer' && !message.pending);

  return (
    <div className="wo-chat">
      <div className="wo-chat-h">
        <span>
          Change notes · <b>{businessName}</b>
        </span>
        <span>{said ? 'Sent · replies within a day' : 'Replies within a day'}</span>
      </div>

      <ul ref={scrollRef} className="wo-msgs">
        <Bubble
          message={{
            id: 'greeting',
            sender: 'team',
            body: greeting,
            createdAt: new Date().toISOString(),
          }}
        />
        {messages.map(message => (
          <Bubble key={message.id} message={message} />
        ))}
      </ul>

      <div className="wo-compose">
        {error && (
          <p role="alert" style={{ marginBottom: 10, color: '#a8271b' }}>
            {error}
          </p>
        )}
        <label htmlFor="wo-change-note">Your note</label>
        <textarea
          id="wo-change-note"
          className="wo-input"
          aria-label="Write a message"
          value={draft}
          onChange={event => {
            setDraft(event.target.value);
            if (error) setError(null);
          }}
          onKeyDown={handleKeyDown}
          maxLength={MAX_LENGTH}
          placeholder="We don't do commercial work — that section can go."
        />
        <div className="wo-compose-row">
          <span>
            {isDemo
              ? 'Prototype — nothing is sent from the demo invite.'
              : 'Goes straight to the person who built it.'}
          </span>
          <button
            type="button"
            className="wo-btn wo-btn--sm"
            onClick={() => void send()}
            disabled={!draft.trim() || sending}
          >
            {sending ? 'Sending…' : 'Send to Mikhail'}
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 8h10M9 4l4 4-4 4" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
